// lib/go-source.ts
// Source-page attribution for internal /go/ links.
//
// The outbound CTAs carry rel="noreferrer", so the browser sends no Referer to
// /go/<slug> and ClickEvent.pageUrl was always empty. Rather than dropping
// `noreferrer` (which would also change what the merchant can see once the
// partner hop is followed), the CTA anchor appends the page it sits on as
// `?src=<path>` and the redirect handler records that.
//
// Only a bare site path is accepted: no scheme, no host, no query string, no
// fragment, a fixed character set and a length cap. A query string is where
// PII (emails, tokens, search terms) lives, so it is never carried.
//
// Kept free of React and next/* so it is unit-testable and safe to import from
// both the client anchor and the server redirect handler.

export const GO_SOURCE_PARAM = "src";
export const MAX_SOURCE_PATH_LENGTH = 200;

// Leading slash, then path segments of URL-safe slug characters. Rejects "//"
// (protocol-relative), backslashes, "..", "%" escapes and anything else.
const SAFE_PATH = /^\/(?:[A-Za-z0-9._~-]+(?:\/[A-Za-z0-9._~-]+)*\/?)?$/;

/** Returns the path if it is a safe, path-only source, otherwise null. */
export function sanitizeSourcePath(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const path = raw.trim();
  if (!path || path.length > MAX_SOURCE_PATH_LENGTH) return null;
  if (!SAFE_PATH.test(path)) return null;
  if (path.split("/").some((seg) => seg === "." || seg === "..")) return null;
  return path;
}

/** True for a same-site /go or /go/<slug> href (relative, not protocol-relative). */
function isInternalGoHref(href: string): boolean {
  if (!href.startsWith("/") || href.startsWith("//") || href.startsWith("/\\")) return false;
  const pathname = href.split(/[?#]/, 1)[0];
  return pathname === "/go" || pathname.startsWith("/go/");
}

/**
 * Append `src=<sourcePath>` to an internal /go/ href. Any other href (external
 * partner URLs, /reviews links, hashes) and any unsafe source path is returned
 * unchanged. An existing `src` is kept.
 */
export function withGoSource(href: string, sourcePath: string | null | undefined): string {
  if (!isInternalGoHref(href)) return href;
  const src = sanitizeSourcePath(sourcePath);
  if (!src) return href;
  const hashAt = href.indexOf("#");
  const base = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : href.slice(hashAt);
  const queryAt = base.indexOf("?");
  const query = new URLSearchParams(queryAt === -1 ? "" : base.slice(queryAt + 1));
  if (query.has(GO_SOURCE_PARAM)) return href;
  query.set(GO_SOURCE_PARAM, src);
  const path = queryAt === -1 ? base : base.slice(0, queryAt);
  return `${path}?${query.toString()}${hash}`;
}

/** Read and validate the source path from a /go/ request's query string. */
export function readGoSource(searchParams: Pick<URLSearchParams, "get">): string | null {
  return sanitizeSourcePath(searchParams.get(GO_SOURCE_PARAM));
}
